import { inject, Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { User } from '../models/user.model';
import { LoginRequest, LoginResponse } from '../models/login.model';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../../environments/environment';
import { LanguageService } from '@core/services/language.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);

  private readonly currentUserSignal = signal<User | null>(null);
  private readonly isAuthenticatedSignal = signal<boolean>(false);

  private readonly API_URL = environment.api.base;
  private readonly AUTH_ENDPOINT = `${this.API_URL}/auth`;
  private readonly TOKEN_STORAGE_KEY = environment.auth.tokenKey;
  private readonly REFRESH_TOKEN_STORAGE_KEY = environment.auth.refreshTokenKey;

  constructor() {
    this.initializeFromLocalStorage();
  }

  get currentUser(): Signal<User | null> {
    return this.currentUserSignal.asReadonly();
  }

  get isAuthenticated(): Signal<boolean> {
    return this.isAuthenticatedSignal.asReadonly();
  }

  checkIsAuthenticated(): boolean {
    return this.isAuthenticatedSignal();
  }
  /**
   * Login method to authenticate the user
   * @param loginRequest
   * @param returnUrl
   */
  login(loginRequest: LoginRequest, returnUrl?: string): Observable<User> {
    return this.http.post<LoginResponse>(`${this.AUTH_ENDPOINT}/login`, loginRequest).pipe(
      tap((response) => {
        // Store the token and refresh token in local storage
        localStorage.setItem(this.TOKEN_STORAGE_KEY, response.token);
        localStorage.setItem(this.REFRESH_TOKEN_STORAGE_KEY, response.refreshToken);

        // update the current user signal
        this.currentUserSignal.set(response.userResponse);
        // update the isAuthenticated signal
        this.isAuthenticatedSignal.set(true);
        // store the user in local storage for remember me
        localStorage.setItem('user', JSON.stringify(response.userResponse));

        // Sauvegarder le rôle pour maintenir la langue après déconnexion
        localStorage.setItem('last-user-role', response.userResponse.role);

        // Réinitialiser la langue selon le rôle de l'utilisateur avec un délai
        setTimeout(() => {
          this.languageService.reinitializeLanguageForUser();
        }, 100);
      }),
      map((response) => response.userResponse),
      tap(() => {
        console.log('Navigating after login. Return URL:', returnUrl);
        setTimeout(() => {
          if (returnUrl && returnUrl !== '') {
            this.router.navigateByUrl(returnUrl).then(
              (success) => console.log('Navigation success:', success),
              (err) => console.error('Navigation error:', err)
            );
          } else {
            this.redirectToDashboard();
          }
        }, 100);
      }),
      catchError((error) => {
        console.error('Login error:', error);

        return throwError(() => error);
      })
    );
  }

  /**
   * Method to check if the user is authenticated
   */
  logout(): void {
    // Remove the token and refresh token from local storage
    localStorage.removeItem(this.TOKEN_STORAGE_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem('user');

    // Update the current user signal
    this.currentUserSignal.set(null);
    // Update the isAuthenticated signal
    this.isAuthenticatedSignal.set(false);

    // Redirect to the login page
    this.router.navigate(['/auth/login']).then(() => {
      console.log('Navigation to login page successful');
    });
  }

  /**
   * refreshToken method to refresh the token
   */
  refreshToken(): Observable<string | null> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_STORAGE_KEY);

    if (!refreshToken) {
      return of(null);
    }

    return this.http
      .post<{ token: string }>(`${this.AUTH_ENDPOINT}/refresh`, { refreshToken: refreshToken })
      .pipe(
        tap((response) => {
          // Store the new token in local storage
          localStorage.setItem(this.TOKEN_STORAGE_KEY, response.token);
        }),
        map((response) => response.token),
        catchError(() => {
          this.logout();
          return of(null);
        })
      );
  }

  /**
   * Redirect to the dashboard based on the user role
   */
  redirectToDashboard(): void {
    const role = this.getUserRole();
    let dashboardUrl = '/';
    console.log('Redirecting to dashboard. User role:', role);
    switch (role) {
      case 'ADMIN':
        dashboardUrl = '/admin/dashboard';
        break;
      case 'OPERATOR':
        dashboardUrl = '/operator/dashboard';
        break;
      case 'DRIVER':
        dashboardUrl = '/driver/dashboard';
        break;
    }
    this.router.navigate([dashboardUrl]).then(() => {
      console.log('Navigation to dashboard successful');
    });
  }

  /**
   * Method to get token from local storage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_STORAGE_KEY);
  }

  /**
   * Method to check if the token is expired
   */
  isTokenExpired(): boolean {
    const token = this.getToken();

    if (!token) {
      return true;
    }

    try {
      interface TokenPayload {
        exp: number;
      }
      const decoded = jwtDecode<TokenPayload>(token);

      return decoded.exp * 1000 < Date.now();
    } catch (error) {
      console.error('Error decoding token:', error);

      return true;
    }
  }

  /**
   * Method to check if the user has a specific role
   * @param role role to check
   */
  hasRole(role: string): boolean {
    const user = this.currentUserSignal();

    if (!user) {
      return false;
    }

    return user.role === role;
  }

  /**
   * Initialize from local storage
   */
  private initializeFromLocalStorage(): void {
    const token = this.getToken();
    const userJson = localStorage.getItem('user');

    if (token && !this.isTokenExpired() && userJson) {
      try {
        const user: User = JSON.parse(userJson) as User;
        this.currentUserSignal.set(user);
        this.isAuthenticatedSignal.set(true);
      } catch (error) {
        console.error('Error parsing user from local storage:', error);
        this.logout();
      }
    } else if (this.isTokenExpired()) {
      this.logout();
    }
  }

  /**
   * Method to get the user role
   */
  getUserRole(): string {
    const user = this.currentUserSignal();
    return user?.role ?? '';
  }
}
